<template>
    <v-dialog :value="isOpen" max-width="600px" @click:outside="onClose" @keydown.esc="onClose">
        <v-card>
            <v-card-title>
                <span class="text-h5">Udostępnij dokument</span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <div class="text-subtitle-1 mb-2">
                                Użytkownicy z dostępem:
                            </div>

                            <v-list dense>
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-list-item-title class="text-body-1">
                                            {{ getUserDisplayName(authorUser || document.createdBy) }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle>
                                            Autor zgłoszenia
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>

                                <v-list-item v-for="user in sharedUsers" :key="user.id">
                                    <v-list-item-content>
                                        <v-list-item-title class="text-body-1">
                                            {{ getUserDisplayName(user) }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action v-if="canRemoveUser(user)">
                                        <v-btn icon small @click="removeUser(user)">
                                            <v-icon small color="error">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </v-list>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-divider class="my-2" />
                            <div class="text-subtitle-1 mb-2">
                                Dodaj użytkowników:
                            </div>
                            <v-autocomplete
                                v-model="selectedUsers"
                                :items="filteredEligibleUsers"
                                :loading="loadingUsers"
                                item-value="id"
                                :item-text="getUserDisplayName"
                                label="Wyszukaj użytkowników"
                                no-data-text="Nie znaleziono użytkowników"
                                clearable
                                outlined
                                dense
                                multiple
                                chips
                                small-chips
                                deletable-chips
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-btn text color="error" @click="onClose">
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn text color="primary" :loading="isLoading" :disabled="isLoading" @click="handleSave">
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'ShareDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        isLoading: {
            type: Boolean
        },
        document: {
            type: Object,
            required: true
        },
        currentUserId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            authorUser: null,
            sharedUsers: [],
            eligibleUsers: [],
            loadingUsers: false,
            selectedUsers: []
        }
    },
    computed: {
        filteredEligibleUsers() {
            const existingUserIds = new Set([
                this.document?.createdBy?.id,
                ...this.sharedUsers.map(u => u.id)
            ])

            return this.eligibleUsers.filter(user => !existingUserIds.has(user.id))
        }
    },
    methods: {
        prepareDialog() {
            return this.initializeDialog()
        },
        async initializeDialog() {
            this.selectedUsers = []
            await this.loadEligibleUsers()

            this.authorUser = this.enrichUser(this.document?.createdBy)
            this.sharedUsers = (this.document.usersWithAccess || []).map(user => this.enrichUser(user))
        },
        async loadEligibleUsers() {
            this.loadingUsers = true
            try {
                const { data } = await this.$store.dispatch('documents/getEligibleUsersForSharing')

                this.eligibleUsers = data.results || []
            } catch (error) {
                this.eligibleUsers = []
            } finally {
                this.loadingUsers = false
            }
        },
        normalizeUser(user) {
            if (!user || typeof user !== 'object') return user

            const firstName = user.firstName || user.userFirstName || user.first_name || ''
            const lastName = user.lastName || user.userLastName || user.last_name || ''

            return {
                ...user,
                firstName,
                lastName
            }
        },
        enrichUser(user) {
            if (!user || typeof user !== 'object') return user

            const eligibleUser = this.eligibleUsers.find(u => u.id === user.id)
            const mergedUser = eligibleUser ? { ...user, ...eligibleUser } : user

            return this.normalizeUser(mergedUser)
        },
        getUserDisplayName(user) {
            if (!user) return ''

            const normalized = this.normalizeUser(user)
            const firstName = normalized?.firstName || ''
            const lastName = normalized?.lastName || ''
            const fullName = `${firstName} ${lastName}`.trim()

            return fullName || normalized.email || normalized.username || 'Brak danych'
        },
        canRemoveUser(user) {
            if (!user) return false

            const isAuthor = user.id === this.document?.createdBy?.id
            const isCurrentUser = user.id === this.currentUserId

            return !isAuthor && !isCurrentUser
        },
        removeUser(user) {
            const index = this.sharedUsers.findIndex(u => u.id === user.id)

            if (index !== -1) {
                this.sharedUsers.splice(index, 1)
            }
        },
        handleSave() {
            const usersToAdd = this.selectedUsers
                .map(userId => this.eligibleUsers.find(u => u.id === userId))
                .filter(Boolean)

            const allSharedUsers = [...this.sharedUsers, ...usersToAdd]

            this.$emit('save', allSharedUsers)
        },
        onClose() {
            if (this.isLoading) return
            this.$emit('close')
        },
    }
}
</script>
